import React, { useMemo } from "react";

import { Link } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";
import { uniqBy, orderBy } from "lodash";

import { SkillFullFragment } from "../../graphql/fragments";
import useGetSkillsAnalytics from "../../graphql/hooks/queries/useGetSkillsAnalytics";
import useGetViewer from "../../graphql/hooks/queries/useGetViewer";

import useTranslations from "../../hooks/useTranslations";
import useFuzzySearch from "../../hooks/useFuzzySearch";

import { AvatarGroup, Button, ButtonGroup, H6, Select } from "../wombat-ui";
import List from "../utils/List";
import SearchInput from "../utils/SearchInput";

import SortAscendingDirectionIcon from "../icons/SortAscendingDirectionIcon";
import SortDescendingDirectionIcon from "../icons/SortDescendingDirectionIcon";

function getSkillUserAnalyticsData(
  skills: SkillFullFragment[],
  userCount: number
) {
  // FIXME: The filter part has to be done because the backend does not
  // handle yet the rights check on reportAnswers
  // sometimes giving empty one despite the havingReportAnswers beeing set to true
  return skills
    ?.filter((skill) => skill.reportAnswers.length > 0)
    .map((skill) => {
      const individualUsers = uniqBy(
        skill.reportAnswers.filter((ra) => ra.isTalent),
        "user.id"
      );

      return {
        ...skill,
        individualUsers,
        acquisitionRate: Math.round((individualUsers.length / userCount) * 100),
      };
    });
}

type SkillsAnalyticsProps = Record<string, never>;

const SkillsAnalytics: React.FC<SkillsAnalyticsProps> = () => {
  const viewer = useGetViewer();
  const t = useTranslations();

  const [{ direction, campaign, q }, setQuery] = useQueryParams({
    direction: StringParam,
    campaign: StringParam,
    q: StringParam,
  });

  const { data, loading } = useGetSkillsAnalytics({
    variables: {
      havingReportAnswers: true,
      campaignId: campaign === "ALL_CAMPAIGNS" ? undefined : campaign,
    },
  });

  const campaigns = useMemo(
    () =>
      [
        {
          name: t("statistics.filters.allCampaigns") as string,
          id: "ALL_CAMPAIGNS",
        },
        ...viewer?.data?.viewer?.company?.campaigns.map(({ name, id }) => ({
          name,
          id,
        })),
      ] || [],
    [viewer]
  );
  const userCount = useMemo(
    () => viewer?.data?.viewer?.company?.users?.length || 0,
    [viewer]
  );
  const userSkills = useMemo(
    () => getSkillUserAnalyticsData(data?.skills?.collection, userCount),
    [data, userCount]
  );

  const sortedUserSkill = useMemo(() => {
    return orderBy(
      userSkills,
      "name",
      (direction?.toLowerCase() as "asc" | "desc") || "asc"
    );
  }, [userSkills, direction]);

  const results = useFuzzySearch(sortedUserSkill, q, {
    keys: ["name"],
    threshold: 0.2,
    useExtendedSearch: true,
    matchIfEmpty: true,
  });

  return (
    <>
      <List
        data={results.flatMap((result) => result.item)}
        listOnly
        loading={loading}
        id={"skill-analytics-list"}
        renderEmptyState={() => {
          return <H6>{t("statistics.noMatchingSkill")}</H6>;
        }}
        renderAsList={(data, index) => (
          <li className="py-4" key={index}>
            <div className="flex items-center space-x-4">
              <div className="flex-1 min-w-0 ">
                <p className="text-sm font-medium truncate text-dark-100">
                  {data.name}
                </p>
              </div>
              <AvatarGroup
                items={data.individualUsers.map((ra) => ({
                  src: ra.user.avatar,
                  type: "user",
                  alt: `${ra.user.name} profile picture`,
                  name: ra.user.name,
                }))}
                size={8}
              />
              <div className="flex flex-col min-w-0 ">
                <p className="text-xs font-medium text-light-900">
                  {t("statistics.skillAcquisitionRate")}
                </p>
                <p className="text-sm font-medium text-right truncate text-dark-100">
                  {data.acquisitionRate}%
                </p>
              </div>
              <Link
                to={`/statistics/skills/${data.id}`}
                id={`${data.id}-link`}
                className="inline-flex items-center shadow-sm px-2.5 py-0.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("users.seeMore")}
              </Link>
            </div>
          </li>
        )}
      >
        <SearchInput
          placeholder={t("statistics.search.skills") as string}
          defaultValue={q}
          onChange={(q) => setQuery({ q })}
        />
        <div className="flex gap-x-2">
          <Select
            id="skill-analytics-group-select"
            label={"Group filter"}
            srOnlyLabel
            items={campaigns}
            renderItem={(i) =>
              i.id === "ALL_CAMPAIGNS"
                ? (t("statistics.filters.allCampaigns") as string)
                : i.name
            }
            searchable
            defaultValue={
              campaigns.find(({ id }) => campaign === id) || campaigns[0]
            }
            onChange={(i) => {
              setQuery({ campaign: i.id });
            }}
            searchOption={{
              keys: ["name"],
              threshold: 0.2,
              useExtendedSearch: true,
            }}
            getValue={(i) => i.name}
          />
          <ButtonGroup scale="sm">
            <Button
              icon={SortAscendingDirectionIcon}
              onClick={() => setQuery({ direction: "ASC" })}
              variant={
                direction === "ASC" || !direction ? "primary" : undefined
              }
            />
            <Button
              icon={SortDescendingDirectionIcon}
              onClick={() => setQuery({ direction: "DESC" })}
              variant={direction === "DESC" ? "primary" : undefined}
            />
          </ButtonGroup>
        </div>
      </List>
    </>
  );
};

export default React.memo(SkillsAnalytics);
